'use client'

import { useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import { useInView, useScrollProgress } from '@/hooks/useInView'
import {
  Users,
  DollarSign,
  Briefcase,
  TrendingUp,
  Bell,
  CheckCircle,
} from 'lucide-react'

/* ── Animated counter hook ─────────────────────────────────── */
function useAnimatedCounter(end: number, duration = 2000, active = false) {
  const [value, setValue] = useState(end)
  useEffect(() => {
    if (!active) return
    const startTime = performance.now()
    function tick(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      setValue(Math.round(end * eased))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [end, duration, active])
  return value
}

/* ── Stat badge data ──────────────────────────────────────── */
const STATS = [
  { icon: Users, label: 'Leads This Week', value: 47, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { icon: DollarSign, label: 'Revenue MTD', value: 187420, prefix: '$', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Briefcase, label: 'Active Jobs', value: 23, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { icon: TrendingUp, label: 'Close Rate', value: 68, suffix: '%', color: 'text-red-400', bg: 'bg-red-500/10' },
]

const NOTIFICATIONS = [
  { text: 'New lead: Mike H. — Roof replacement', delay: 1.8 },
  { text: 'Proposal signed: Williams Residence', delay: 3.5 },
  { text: 'Payment received: $12,400', delay: 5.2 },
]

/* ── Main Component ───────────────────────────────────────── */
export function DashboardMockup() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLDivElement>(null)
  const { ref: inViewRef, isInView } = useInView()

  // Scroll-driven parallax
  const progress = useScrollProgress(sectionRef)

  // Mouse-follow 3D tilt (desktop only)
  const [mouseX, setMouseX] = useState(0)
  const [mouseY, setMouseY] = useState(0)

  // Cache rect to avoid repeated getBoundingClientRect() on every mousemove
  const cachedRect = useRef<DOMRect | null>(null)

  function handleMouseMove(e: React.MouseEvent) {
    if (!imageRef.current) return
    // Measure once on first entry, invalidate on leave
    if (!cachedRect.current) {
      cachedRect.current = imageRef.current.getBoundingClientRect()
    }
    const rect = cachedRect.current
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    setMouseX(x)
    setMouseY(y)
  }

  function handleMouseLeave() {
    setMouseX(0)
    setMouseY(0)
    cachedRect.current = null // re-measure next time (handles scroll between visits)
  }

  // Stat counters
  const leads = useAnimatedCounter(47, 2000, isInView)
  const revenue = useAnimatedCounter(187420, 2500, isInView)
  const jobs = useAnimatedCounter(23, 1800, isInView)
  const closeRate = useAnimatedCounter(68, 2200, isInView)
  const counterValues = [leads, revenue, jobs, closeRate]

  // Mouse-follow tilt transforms
  const rotateX = mouseY * -6 // maps [-0.5, 0.5] -> [3, -3]
  const rotateY = mouseX * -6 // maps [-0.5, 0.5] -> [-3, 3] (inverted by negation)

  return (
    <section ref={sectionRef} className="section relative overflow-hidden">
      {/* Subtle gradient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-600/[0.04] rounded-full blur-[120px] pointer-events-none" />

      <div ref={inViewRef} className="section-inner">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-14 anim-fade-in-up ${isInView ? 'in-view' : ''}`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-3">
            Your Command Center
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-3xl mx-auto">
            Everything You Need,{' '}
            <span className="gradient-text-red">One Dashboard</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Leads, jobs, revenue, crews — all in real time. No more tab-switching chaos.
          </p>
        </div>

        {/* Dashboard with scroll-driven parallax (y+scale only — compositor-safe) */}
        <div
          ref={imageRef}
          style={{
            transform: `translateY(${progress * 60 - 30}px) scale(${0.92 + progress * 0.08})`,
            // rotateX removed — 3D perspective forces reflow (PSI Round 2 diagnostic)
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="relative max-w-5xl mx-auto will-change-transform"
        >
          <div
            style={{
              transform: `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
              transition: 'transform 0.15s ease-out',
            }}
            className={`relative anim-scale-in anim-delay-2 ${isInView ? 'in-view' : ''}`}
          >
            {/* Ambient glow */}
            <div className="absolute -inset-8 bg-gradient-to-t from-red-600/10 via-red-600/5 to-transparent rounded-3xl blur-2xl pointer-events-none" />

            <div className="relative bg-zinc-950 border border-zinc-800/60 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 glow-red">
              {/* Browser Chrome */}
              <div className="flex items-center gap-2 px-4 sm:px-5 py-3 bg-zinc-950/90 border-b border-zinc-800/50">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <div className="w-3 h-3 rounded-full bg-green-500/70" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-48 sm:w-60 h-5 bg-zinc-800 rounded-md mx-auto flex items-center justify-center">
                    <span className="text-[10px] text-zinc-600 hidden sm:inline">
                      app.builderlync.com/dashboard
                    </span>
                  </div>
                </div>
                {/* Live indicator */}
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                  <span className="text-[10px] text-zinc-600 hidden sm:inline">
                    Live
                  </span>
                </div>
              </div>

              {/* Dashboard Image */}
              <Image
                src="/assets/images/software-shots/builderlync-dash.webp"
                alt="BuilderLync dashboard interface showing CRM, job management, and AI automation features"
                width={1302}
                height={714}
                priority
                className="w-full h-auto"
                sizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1080px, 1280px"
              />
            </div>

            {/* ── Floating Notification Feed ─────────────────── */}
            <div className="absolute top-16 -right-2 sm:-right-6 lg:-right-12 z-20 hidden md:block">
              {NOTIFICATIONS.map((notif, i) => (
                <div
                  key={notif.text}
                  className={`glass-card px-4 py-2.5 flex items-center gap-2.5 mb-2 min-w-[240px] max-w-[280px] anim-fade-in-right anim-delay-${i + 4} ${isInView ? 'in-view' : ''}`}
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <CheckCircle size={12} className="text-emerald-400" />
                  </div>
                  <span className="text-xs text-zinc-300 truncate">{notif.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Live Stats Strip ──────────────────────────────── */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mt-8 sm:mt-10 anim-fade-in-up anim-delay-3 ${isInView ? 'in-view' : ''}`}
        >
          {STATS.map((stat, i) => {
            const Icon = stat.icon
            const val = counterValues[i]
            const display =
              stat.prefix === '$'
                ? `$${val.toLocaleString()}`
                : stat.suffix
                ? `${val}${stat.suffix}`
                : `${val}`

            return (
              <div
                key={stat.label}
                className={`glass-card p-4 sm:p-5 group hover:border-red-600/20 transition-colors duration-300 anim-fade-in-up anim-delay-${i + 4} ${isInView ? 'in-view' : ''}`}
              >
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center transition-colors group-hover:bg-opacity-20`}>
                    <Icon size={16} className={stat.color} />
                  </div>
                  <span className="text-[11px] text-zinc-500 uppercase tracking-wider font-medium">
                    {stat.label}
                  </span>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-white tabular-nums block">
                  {display}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
